class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');

    this.detailsContainer.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    // Use forceClose for the × button so it always works regardless of search state
    this.querySelector('button[type="button"]').addEventListener('click', this.forceClose.bind(this));

    // Safari on iOS natively removes <open> from <details> when a form inside submits.
    // This fires BEFORE the JS submit event, so _submitting isn't set yet.
    // If predictive-search results are showing, re-open immediately.
    this.detailsContainer.addEventListener('toggle', () => {
      if (!this.detailsContainer.hasAttribute('open') && !this._legitimateClose) {
        const ps = this.querySelector('predictive-search');
        if (ps && ps.getAttribute('results') === 'true') {
          this.detailsContainer.setAttribute('open', '');
        }
      }
    });

    this.summaryToggle.setAttribute('role', 'button');
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    // Don't close when iOS keyboard Search button was pressed
    const ps = this.querySelector('predictive-search');
    if (ps && ps._submitting) return;
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }

  _getMobileOverlay() {
    if (!this._mobileOverlay) {
      const el = document.createElement('div');
      el.style.cssText = [
        'position:fixed',
        'top:0',
        'left:0',
        'right:0',
        'bottom:0',
        'background:rgba(0,0,0,0.45)',
        // z-index 2: above page content (0-1), below sticky header section (z-index 3)
        // so the search card inside the header still shows above the overlay
        'z-index:2',
        'display:none',
        '-webkit-tap-highlight-color:transparent',
      ].join(';');
      el.addEventListener('click', () => this.forceClose());
      document.body.appendChild(el);
      this._mobileOverlay = el;
    }
    return this._mobileOverlay;
  }

  _lockScroll() {
    // iOS Safari ignores overflow:hidden on body — use position:fixed instead
    this._savedScrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this._savedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
  }

  _unlockScroll() {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    window.scrollTo(0, this._savedScrollY || 0);
  }

  open(event) {
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    event.target.closest('details').setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);

    if (window.matchMedia('(max-width: 749px)').matches) {
      this._lockScroll();
      this._getMobileOverlay().style.display = 'block';
    } else {
      document.body.classList.add('overflow-hidden');
    }

    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]'),
      this.detailsContainer.querySelector('input:not([type="hidden"])')
    );
  }

  // Called by the × close button — bypasses all guards so the modal always closes
  forceClose() {
    this._forceClose = true;
    this.close();
    this._forceClose = false;
  }

  close(focusToggle = true) {
    // Block accidental close from keyboard Search button (iOS)
    if (!this._forceClose) {
      const ps = this.querySelector('predictive-search');
      if (ps && ps._submitting) return;
    }
    // Mark this as an intentional close so the toggle listener doesn't re-open
    this._legitimateClose = true;
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute('open');
    this._legitimateClose = false;
    document.body.removeEventListener('click', this.onBodyClickEvent);

    if (window.matchMedia('(max-width: 749px)').matches) {
      if (this._mobileOverlay) this._mobileOverlay.style.display = 'none';
      this._unlockScroll();
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  }
}

customElements.define('details-modal', DetailsModal);
